<script setup lang="ts">
/**
 * Строка стека — макеты 9-1 (298:725) и 9-8: номер, ручка переноса, тип, ключ,
 * заголовок и срок. Верхняя строка выделена: это текущая работа, и вся
 * метафора плана держится на том, что её видно с первого взгляда.
 *
 * На телефоне ручки переноса нет — там строка складывается в две линии и
 * порядок меняют кнопками (макет 9-8): тащить строку пальцем по списку, где
 * помещается пять из тридцати, значит промахиваться.
 *
 * Кнопки «убрать» ни в одном макете нет, а убирать из плана надо: она
 * появляется по наведению поверх срока и статичную раскладку не двигает.
 */
import { ArrowDown, ArrowUp, GripVertical, X } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { formatDueDate } from '@/features/issues/format'

import type { StackItem } from '../api'

const props = defineProps<{
  item: StackItem
  /** Номер в плане, с единицы: «третья в плане», а не «индекс два» */
  position: number
  /** Верх стека — текущая работа */
  current?: boolean
  /** Телефон: две строки и кнопки порядка вместо переноса мышью */
  compact?: boolean
  /** Первая и последняя строки: соответствующая кнопка порядка им не нужна */
  first?: boolean
  last?: boolean
}>()

const emit = defineEmits<{ remove: []; up: []; down: [] }>()

const { t, locale } = useI18n()

/** Просрочка — срок раньше сегодняшнего дня; сегодняшний срок ещё не долг */
const isOverdue = computed(() => {
  if (!props.item.dueDate) return false

  const due = new Date(props.item.dueDate)

  if (Number.isNaN(due.getTime())) return false

  const now = new Date()

  return due.getTime() < new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
})

/** Прочерк, а не пустота: пустое место справа читается как обрезанная строка */
const dueLabel = computed(() => {
  if (isOverdue.value) return t('plan.row.overdue')

  return props.item.dueDate ? formatDueDate(props.item.dueDate, locale.value) : '—'
})
</script>

<template>
  <div
    class="prow"
    :class="{ 'prow--current': current, 'prow--compact': compact }"
    :data-key="item.key"
  >
    <span class="prow__num">{{ position }}</span>

    <GripVertical v-if="!compact" class="prow__grip" :size="16" aria-hidden="true" />

    <IssueTypeIcon v-if="!compact" class="prow__type" :type="item.type" />

    <RouterLink class="prow__key" :to="`/issue/${item.key}`">{{ item.key }}</RouterLink>

    <RouterLink v-if="!compact" class="prow__title" :to="`/issue/${item.key}`" :title="item.title">
      {{ item.title }}
    </RouterLink>

    <span v-if="current && !compact" class="prow__now">{{ t('plan.row.now') }}</span>

    <span class="prow__due" :class="{ 'prow__due--overdue': isOverdue }">{{ dueLabel }}</span>

    <button
      v-if="!compact"
      type="button"
      class="prow__remove"
      :title="t('plan.row.remove')"
      :aria-label="t('plan.row.remove')"
      @click="emit('remove')"
    >
      <X :size="14" />
    </button>

    <template v-if="compact">
      <RouterLink class="prow__title" :to="`/issue/${item.key}`">{{ item.title }}</RouterLink>

      <div class="prow__order">
        <button
          type="button"
          class="prow__order-button"
          :disabled="first"
          :aria-label="t('plan.row.up')"
          @click="emit('up')"
        >
          <ArrowUp :size="14" />
        </button>
        <button
          type="button"
          class="prow__order-button"
          :disabled="last"
          :aria-label="t('plan.row.down')"
          @click="emit('down')"
        >
          <ArrowDown :size="14" />
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.prow {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 14px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

/* Текущая работа: заливка тинтом и рамка акцентом (макет 298:740) */
.prow--current {
  border-color: var(--color-primary);
  background: var(--color-primary-tint);
}

.prow__num {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
}

.prow__grip {
  flex-shrink: 0;
  color: var(--color-text-muted);
  cursor: grab;
}

.prow__type {
  flex-shrink: 0;
}

.prow__key {
  flex-shrink: 0;
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  text-decoration: none;
}

.prow__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
  line-height: 20px;
  white-space: nowrap;
  text-decoration: none;
  text-overflow: ellipsis;
}

.prow--current .prow__title {
  font-weight: var(--font-weight-semibold);
}

.prow__now {
  flex-shrink: 0;
  color: var(--color-primary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.prow__due {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.prow__due--overdue {
  color: var(--color-danger);
}

/* Кнопка стоит поверх срока и залита фоном строки: так наведение не двигает
   ни одной буквы из тех, что нарисованы в макете. */
.prow__remove {
  position: absolute;
  top: 50%;
  right: 8px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-muted);
  transform: translateY(-50%);
  cursor: pointer;
}

.prow--current .prow__remove {
  background: var(--color-primary-tint);
}

.prow:hover .prow__remove,
.prow__remove:focus-visible {
  display: inline-flex;
}

.prow__remove:hover {
  color: var(--color-danger);
}

/* Телефон (макет 9-8): номер и ключ первой строкой, срок справа, заголовок с
   кнопками порядка — второй. */
.prow--compact {
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  align-items: center;
  gap: 4px 8px;
  padding: 10px 12px;
}

.prow--compact .prow__due {
  grid-column: 4;
}

.prow--compact .prow__title {
  grid-column: 1 / 4;
  white-space: normal;
}

.prow--compact .prow__order {
  display: flex;
  grid-column: 4;
  gap: 6px;
}

.prow__order-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.prow__order-button:disabled {
  color: var(--color-text-disabled);
  cursor: default;
}
</style>
